
class AgentApiService {
  constructor() {
    this.baseURL = 'https://app.akiya2.com';
    
    // Check if we're in a browser environment before accessing localStorage
    if (typeof window !== 'undefined' && window.localStorage) {
      this.token = localStorage.getItem('agent_token') || 
                   localStorage.getItem('access_token') || 
                   sessionStorage.getItem('agent_token');
    } else {
      this.token = null;
    }
    
    console.log('AgentApiService initialized with token:', this.token ? 'Present' : 'None');
  }

  // Token management with browser checks
  setToken(token) {
    console.log('Setting token:', token ? 'Present' : 'None');
    this.token = token;
    
    // Only access localStorage/sessionStorage in browser environment
    if (typeof window !== 'undefined') {
      if (token) {
        localStorage.setItem('agent_token', token);
        localStorage.setItem('access_token', token);
        sessionStorage.setItem('agent_token', token);
      } else {
        this.clearToken();
      }
    }
  }

  clearToken() {
    console.log('Clearing all tokens');
    this.token = null;
    
    // Only access localStorage/sessionStorage in browser environment
    if (typeof window !== 'undefined') {
      localStorage.removeItem('agent_token');
      localStorage.removeItem('access_token');
      sessionStorage.removeItem('agent_token');
    }
  }

  isAuthenticated() {
    const hasToken = !!this.token;
    console.log('Is authenticated:', hasToken);
    return hasToken;
  }

  getHeaders(includeContentType = true) {
    const headers = {};
    if (includeContentType) {
      headers['Content-Type'] = 'application/json';
    }
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }
    return headers;
  }

  // Alternative method to initialize token after component mounts
  initializeToken() {
    if (typeof window !== 'undefined' && window.localStorage) {
      this.token = localStorage.getItem('agent_token') || 
                   localStorage.getItem('access_token') || 
                   sessionStorage.getItem('agent_token');
      console.log('Token initialized after mount:', this.token ? 'Present' : 'None');
    }
  }

  // Generic request methods (same as before)
  async makeRequest(url, options = {}) {
    const fullUrl = `${this.baseURL}${url}`;
    console.log(`Making ${options.method || 'GET'} request to:`, fullUrl);
    
    try {
      const response = await fetch(fullUrl, {
        ...options,
        headers: {
          ...this.getHeaders(!options.body || typeof options.body === 'string'),
          ...options.headers,
        },
      });

      console.log(`Response status: ${response.status} for ${fullUrl}`);

      const contentType = response.headers.get('content-type');
      let data;
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      if (!response.ok) {
        console.error(`HTTP Error ${response.status}:`, data);
        
        if (response.status === 401) {
          console.log('Authentication failed, clearing token');
          this.clearToken();
          throw new Error('Authentication failed. Please login again.');
        }
        if (response.status === 403) {
          throw new Error('Access denied.');
        }
        if (response.status === 404) {
          throw new Error('Resource not found.');
        }
        
        const errorMessage = data?.detail || data?.message || `HTTP error! status: ${response.status}`;
        throw new Error(errorMessage);
      }

      console.log(`Successful response from ${fullUrl}:`, data);
      return data;
    } catch (error) {
      console.error('API Request failed for', fullUrl, ':', error);
      throw error;
    }
  }

  async get(endpoint) {
    return this.makeRequest(endpoint, { method: 'GET' });
  }

  async post(endpoint, data) {
    return this.makeRequest(endpoint, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async put(endpoint, data) {
    return this.makeRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async delete(endpoint) {
    return this.makeRequest(endpoint, { method: 'DELETE' });
  }

  async postFormData(endpoint, formData) {
    return this.makeRequest(endpoint, {
      method: 'POST',
      body: formData,
      headers: this.token ? { 'Authorization': `Bearer ${this.token}` } : {}
    });
  }

  // ============================
  // AUTHENTICATION ENDPOINTS
  // ============================

  async login(credentials) {
    const response = await this.post('/agents/login', credentials);
    if (response.access_token) {
      this.setToken(response.access_token);
    }
    return response;
  }

  async registerAgent(agentData) {
    const response = await this.post('/agents/register-agent', agentData);
    if (response.access_token) {
      this.setToken(response.access_token);
    }
    return response;
  }

  // ============================
  // PROFILE ENDPOINTS
  // ============================

  // FIXED: Added missing await
  async getCurrentProfile() {
    try {
      console.log('Attempting to get enhanced agent profile...');
      const data = await this.get('/agents/get-enhanced-agent-profile'); // ✅ Fixed missing await
      console.log('Enhanced agent profile data:', data);
      return data;
    } catch (error) {
      console.error('Failed to get agent profile:', error);
      if (error.message.includes('not found') || error.message.includes('404')) {
        console.log('Falling back to user profile...');
        return await this.get('/agents/get-enhanced-user-profile');
      }
      throw error;
    }
  }

  async getCurrentUserProfile() {
    return this.get('/agents/get-enhanced-user-profile');
  }

  async updateAgentProfile(updateData) {
    return this.put('/agents/update-agent-data', updateData);
  }

  // ============================
  // NEW LISTING ENDPOINTS APPROACH
  // ============================

  // NEW: Get listings by IDs from agent data
  async getListingsByIds(propertyIds) {
    console.log('🔍 Getting listings for IDs:', propertyIds);
    if (!propertyIds || propertyIds.length === 0) {
      console.log('❌ No property IDs provided');
      return { listings: [] };
    }
    
    try {
      console.log('📡 Making API call to /listings_by_ids with payload:', {
        property_ids: propertyIds
      });
      
      const response = await this.post('/listings_by_ids', {
        property_ids: propertyIds
      });
      
      console.log('✅ Listings by IDs response:', response);
      console.log('📊 Response structure:', Object.keys(response || {}));
      console.log('🏠 Listings array length:', response?.listings?.length || 0);
      
      return response;
    } catch (error) {
      console.error('❌ Failed to get listings by IDs:', error);
      console.error('🚨 Error details:', {
        message: error.message,
        stack: error.stack
      });
      throw error;
    }
  }

  // UPDATED: New approach for getting agent listings
  async getMyListings() {
    try {
      console.log('🚀 Getting agent listings using new approach...');
      
      // Step 1: Get agent profile to get listings_added array
      const agentProfile = await this.getCurrentProfile();
      console.log('👤 Agent profile loaded:', {
        isAgent: agentProfile.isAgent,
        agentId: agentProfile.agent_id,
        listingsAddedCount: agentProfile.listings_added?.length || 0,
        listingsAdded: agentProfile.listings_added
      });
      
      if (!agentProfile.isAgent) {
        console.log('❌ User is not an agent');
        return { listings: [], total_listings: 0 };
      }
      
      const listingsAdded = agentProfile.listings_added || [];
      console.log('📋 Listings added from profile:', listingsAdded);
      
      if (listingsAdded.length === 0) {
        console.log('📭 No listings found in agent profile');
        return { 
          listings: [], 
          total_listings: 0,
          approved_listings: 0,
          pending_listings: 0,
          featured_listings: 0
        };
      }
      
      // Step 2: Get actual listing data by IDs
      console.log('🔍 Attempting to get listings by IDs...');
      const listingsResponse = await this.getListingsByIds(listingsAdded);
      
      // ✅ FIXED: API returns data in 'data' field, not 'listings'
      const listings = listingsResponse.data || listingsResponse.listings || [];
      
      console.log('📦 Raw listings from API:', {
        responseKeys: Object.keys(listingsResponse || {}),
        requestedCount: listingsResponse.requested_count,
        foundCount: listingsResponse.found_count,
        notFoundCount: listingsResponse.not_found_count,
        notFoundIds: listingsResponse.not_found_ids,
        listingsCount: listings.length,
        firstListing: listings[0] ? Object.keys(listings[0]) : 'No listings'
      });
      
      // Log any missing listings
      if (listingsResponse.not_found_ids && listingsResponse.not_found_ids.length > 0) {
        console.warn('⚠️ Some listings not found:', listingsResponse.not_found_ids);
      }
      
      // Step 3: Process and enhance the listings data
      console.log('⚙️ Processing listings data...');
      const processedListings = this.processAgentListings(listings);
      
      // Step 4: Calculate stats
      console.log('📊 Calculating stats...');
      const stats = this.calculateListingStats(processedListings);
      
      console.log('✅ Final processed data:', {
        processedListingsCount: processedListings.length,
        stats: stats
      });
      
      const finalResponse = {
        listings: processedListings,
        total_listings: processedListings.length,
        requested_count: listingsResponse.requested_count,
        found_count: listingsResponse.found_count,
        not_found_count: listingsResponse.not_found_count,
        not_found_ids: listingsResponse.not_found_ids,
        ...stats
      };
      
      console.log('🎯 Final response being returned:', finalResponse);
      
      return finalResponse;
      
    } catch (error) {
      console.error('💥 Failed to get agent listings:', error);
      console.error('🚨 Full error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      throw error;
    }
  }

  // NEW: Process listings data to add status and other fields
  processAgentListings(listings) {
    return listings.map(listing => {
      // The listing already has status from the API response
      let status = listing.status || 'approved'; // Default to approved if no status
      let underCheck = false;
      
      // Set underCheck based on status
      if (status === 'pending_approval' || status === 'under_review') {
        underCheck = true;
      }
      
      // Ensure consistent field names (API already returns correct format)
      const processedListing = {
        ...listing,
        status: status,
        underCheck: underCheck,
        // Ensure these fields exist for frontend compatibility
        property_id: listing.property_id || listing.id,
        Address: listing.Address || listing.address,
        Price: listing.Price || listing.price,
        Images: listing.Images || listing.images || [],
        Prefecture: listing.Prefecture || listing.prefecture,
        Municipality: listing.Municipality || listing.municipality,
        Year_of_Construction: listing.Year_of_Construction || listing.year_of_construction,
        Room_Type: listing.Room_Type || listing.room_type,
        featured: listing.featured || false,
        createdAt: listing.createdAt || listing.created_at || listing.dateAdded
      };
      
      console.log('🔧 Processed listing:', {
        id: processedListing.property_id,
        status: processedListing.status,
        underCheck: processedListing.underCheck,
        featured: processedListing.featured
      });
      
      return processedListing;
    });
  }

  // NEW: Calculate listing statistics
  calculateListingStats(listings) {
    const stats = {
      approved_listings: 0,
      pending_listings: 0,
      rejected_listings: 0,
      featured_listings: 0
    };
    
    listings.forEach(listing => {
      // Count approved
      if (listing.status === 'approved' || (!listing.underCheck && listing.status !== 'pending_approval')) {
        stats.approved_listings++;
      }
      
      // Count pending
      if (listing.status === 'pending_approval' || listing.underCheck) {
        stats.pending_listings++;
      }
      
      // Count rejected
      if (listing.status === 'rejected') {
        stats.rejected_listings++;
      }
      
      // Count featured
      if (listing.featured) {
        stats.featured_listings++;
      }
    });
    
    return stats;
  }

  // Keep old method for backward compatibility
  async getMyListingsOld() {
    return this.get('/agents/my-listings-with-status');
  }

  // ============================
  // OTHER LISTING ENDPOINTS
  // ============================

  async addListing(formData) {
    return this.postFormData('/agents/add-listing', formData);
  }

  async editListing(propertyId, updateData) {
    return this.put(`/agents/edit-listing/${propertyId}`, updateData);
  }

  async uploadPropertyImages(propertyId, formData) {
    return this.postFormData('/agents/upload-property-images', formData);
  }

  async uploadPropertyPDF(propertyId, formData) {
    return this.postFormData('/agents/upload-property-pdf', formData);
  }

  // ============================
  // AGENT CONVERSION ENDPOINTS
  // ============================

  async convertUserToAgent(agentData) {
    return this.post('/agents/convert-user-to-agent1', agentData);
  }

  async restoreAgentStatus() {
    return this.post('/agents/restore-agent-status', {});
  }

  async convertAgentToUser() {
    return this.post('/agents/convert-agent-to-user', {});
  }

  async checkAgentData() {
    try {
      const response = await this.get('/agents/check-agent-conversion-data');
      return response;
    } catch (error) {
      return { can_restore_agent_data: false };
    }
  }

  // ============================
  // PUBLIC LISTINGS
  // ============================

  async getPublicListings() {
    return this.get('/agents/public/approved-listings');
  }

  async getPublicListingsByMunicipality(municipality) {
    return this.get(`/agents/public/approved-listings/${municipality}`);
  }

  // ============================
  // SUPER ADMIN ENDPOINTS
  // ============================

  async getPendingListings() {
    return this.get('/agents/admin/pending-listings');
  }

  async approveOrRejectListing(propertyId, action) {
    return this.post('/agents/admin/approve-listing', {
      property_id: propertyId,
      action: action
    });
  }

  async deleteListing(propertyId) {
    return this.delete(`/agents/admin/delete-listing/${propertyId}`);
  }

  async getAllListingsAdmin() {
    return this.get('/agents/admin/all-listings');
  }

  async getAllUsersAdmin() {
    return this.get('/agents/admin/all-users');
  }

  async getAllAgentsAdmin() {
    return this.get('/agents/admin/all-agents');
  }

  async verifyAgent(agentId) {
    return this.post(`/agents/admin/verify-agent/${agentId}`, {});
  }

  async unverifyAgent(agentId) {
    return this.post(`/agents/admin/unverify-agent/${agentId}`, {});
  }

  async promoteToSuperAdmin(email) {
    return this.post('/agents/admin/promote-to-superadmin', { email });
  }

  async demoteFromSuperAdmin(email) {
    return this.post('/agents/admin/demote-from-superadmin', { email });
  }

  async getSuperAdminList() {
    return this.get('/agents/admin/superadmin-list');
  }

  // ============================
  // STATS AND ANALYTICS
  // ============================

  async getAgentStats() {
    try {
      const listings = await this.getMyListings();
      return {
        total: listings.total_listings || 0,
        approved: listings.approved_listings || 0,
        pending: listings.pending_listings || 0,
        featured: listings.featured_listings || 0
      };
    } catch (error) {
      console.error('Failed to get agent stats:', error);
      return { total: 0, approved: 0, pending: 0, featured: 0 };
    }
  }

  async getAdminStats() {
    try {
      const [pendingListings, allListings, allAgents] = await Promise.all([
        this.getPendingListings(),
        this.getAllListingsAdmin(),
        this.getAllAgentsAdmin()
      ]);

      return {
        totalListings: allListings.total_listings || allListings.listings?.length || 0,
        pendingListings: pendingListings.total_pending || pendingListings.listings?.length || 0,
        approvedListings: allListings.approved_listings || 0,
        totalAgents: allAgents.total_agents || allAgents.agents?.length || 0,
        verifiedAgents: allAgents.verified_agents || 0
      };
    } catch (error) {
      console.error('Failed to get admin stats:', error);
      return {
        totalListings: 0,
        pendingListings: 0,
        approvedListings: 0,
        totalAgents: 0,
        verifiedAgents: 0
      };
    }
  }
}

// Create and export a singleton instance
const agentApiService = new AgentApiService();
export default agentApiService;